import {
  claimTask,
  fetchTasks,
  type TaskClaimResult,
  type TaskRow,
  type TasksListResponse,
} from "./api";
import { applyDotsFromApi } from "../dots-currency";
import { notifyBattlePassXp } from "../battle-pass/fx";

function formatProgress(task: TaskRow): string {
  const goal = Math.max(1, Math.floor(task.goalValue));
  if (task.modeId === "speedrun" || task.modeId === "penalty") {
    if (task.progressValue <= 0) return `Best: — / under ${goal}s`;
    const sec = task.progressValue / 100;
    return `Best: ${sec.toFixed(2)}s / under ${goal}s`;
  }
  if (task.metric === "time") {
    return `${Math.floor(task.progressValue)}s / ${goal}s`;
  }
  if (task.modeId === "progression") {
    return `Lv ${Math.floor(task.progressValue)} / ${goal}`;
  }
  return `${Math.floor(task.progressValue)} / ${goal}`;
}

function playClaimSurprise(result: TaskClaimResult) {
  const overlay = document.createElement("div");
  overlay.className = "taskClaimSurprise";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  const panel = document.createElement("div");
  panel.className = "taskClaimSurprisePanel";
  const title = document.createElement("h2");
  title.className = "taskClaimSurpriseTitle";
  title.textContent = "REWARD!";
  const label = document.createElement("p");
  label.className = "taskClaimSurpriseLabel";
  label.textContent = result.rewardLabel;
  panel.append(title, label);
  if (result.rewardCosmetic?.imageData) {
    const img = document.createElement("img");
    img.className = "taskClaimSurpriseArt";
    img.alt = result.rewardCosmetic.name;
    img.src = result.rewardCosmetic.imageData.startsWith("data:")
      ? result.rewardCosmetic.imageData
      : `data:image/png;base64,${result.rewardCosmetic.imageData}`;
    panel.appendChild(img);
  } else if (result.rewardKind === "dots") {
    const dotsHint = document.createElement("p");
    dotsHint.className = "taskClaimSurpriseHint";
    dotsHint.textContent = `+${Math.max(0, Math.floor(Number(result.rewardDots) || 0))} Dots`;
    panel.appendChild(dotsHint);
  } else if (result.rewardKind === "free_crate") {
    const crateHint = document.createElement("p");
    crateHint.className = "taskClaimSurpriseHint";
    crateHint.textContent = `Free rolls ready: ${result.freeCrateRollsLeft}`;
    panel.appendChild(crateHint);
  }
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "btn green";
  closeBtn.textContent = "Nice!";
  const close = () => overlay.remove();
  closeBtn.onclick = close;
  overlay.onclick = (e) => {
    if (e.target === overlay) close();
  };
  panel.appendChild(closeBtn);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add("taskClaimSurprise--show"));
}

export type TasksScreenApi = {
  refresh: () => Promise<void>;
  getClaimableCount: () => number;
};

export function mountTasksScreen(
  root: HTMLElement,
  opts: {
    isSignedIn: () => boolean;
    onClaimableChange?: (count: number) => void;
    onNeedSignIn?: () => void;
  },
): TasksScreenApi {
  let claimableCount = 0;
  let loading = false;

  const inner = document.createElement("div");
  inner.className = "tasksScreenInner";
  const heading = document.createElement("h1");
  heading.textContent = "TASKS";
  const hint = document.createElement("p");
  hint.className = "accessHint";
  hint.id = "tasksLoadingHint";
  const list = document.createElement("div");
  list.className = "tasksList";
  list.setAttribute("role", "list");
  inner.append(heading, hint, list);
  root.replaceChildren(inner);

  function setClaimable(n: number) {
    claimableCount = Math.max(0, Math.floor(n));
    opts.onClaimableChange?.(claimableCount);
  }

  function renderSection(title: string, tasks: TaskRow[]) {
    const section = document.createElement("section");
    section.className = "tasksSection";
    const h = document.createElement("h2");
    h.className = "tasksSectionTitle";
    h.textContent = title;
    section.appendChild(h);
    if (!tasks.length) {
      const empty = document.createElement("p");
      empty.className = "accessHint";
      empty.textContent = "No tasks in this section right now.";
      section.appendChild(empty);
      return section;
    }
    for (const task of tasks) {
      const card = document.createElement("article");
      card.className = "taskCard";
      card.setAttribute("role", "listitem");
      if (task.claimable) {
        card.classList.add("taskCard--claimable");
        card.tabIndex = 0;
        card.setAttribute("role", "button");
        card.setAttribute("aria-label", `Claim reward for ${task.description}`);
      } else if (task.claimed) {
        card.classList.add("taskCard--claimed");
      }
      const main = document.createElement("div");
      main.className = "taskCardMain";
      const desc = document.createElement("p");
      desc.className = "taskCardDesc";
      desc.textContent = task.description;
      const progress = document.createElement("p");
      progress.className = "taskCardProgress";
      progress.textContent = formatProgress(task);
      const reward = document.createElement("p");
      reward.className = "taskCardReward";
      reward.textContent = `Reward: ${task.rewardLabel}`;
      main.append(desc, progress, reward);
      card.appendChild(main);
      if (task.claimable) {
        const claim = document.createElement("span");
        claim.className = "taskCardClaim";
        claim.textContent = "CLAIM";
        card.appendChild(claim);
        const doClaim = async () => {
          if (loading) return;
          loading = true;
          card.classList.add("taskCard--claiming");
          try {
            const result = await claimTask(task.id);
            if (result.dots) applyDotsFromApi(result.dots as Parameters<typeof applyDotsFromApi>[0]);
            if (result.battlePass) notifyBattlePassXp(result.battlePass);
            playClaimSurprise(result);
            setClaimable(result.claimableCount);
            await refresh();
          } catch (e) {
            hint.textContent = e instanceof Error ? e.message : "Claim failed.";
          } finally {
            loading = false;
            card.classList.remove("taskCard--claiming");
          }
        };
        card.onclick = () => void doClaim();
        card.onkeydown = (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            void doClaim();
          }
        };
      } else if (task.claimed) {
        const claimed = document.createElement("span");
        claimed.className = "taskCardClaim taskCardClaim--claimed";
        claimed.textContent = "CLAIMED";
        card.appendChild(claimed);
      }
      section.appendChild(card);
    }
    return section;
  }

  async function refresh() {
    if (!opts.isSignedIn()) {
      list.replaceChildren();
      hint.textContent = "Sign in to track and claim Tasks.";
      setClaimable(0);
      opts.onNeedSignIn?.();
      return;
    }
    hint.textContent = "Loading…";
    hint.classList.remove("hidden");
    try {
      const data: TasksListResponse = await fetchTasks();
      list.replaceChildren(
        renderSection("Daily Tasks (5 today)", data.daily),
        renderSection("Weekly Tasks (up to 10)", data.weekly),
        renderSection("Permanent Tasks", data.permanent),
      );
      setClaimable(data.claimableCount);
      hint.textContent = "";
      hint.classList.add("hidden");
    } catch (e) {
      hint.textContent = e instanceof Error ? e.message : "Failed to load Tasks.";
      hint.classList.remove("hidden");
    }
  }

  return {
    refresh,
    getClaimableCount: () => claimableCount,
  };
}

export function syncTasksClaimBadge(btn: HTMLElement | null, count: number) {
  if (!btn) return;
  let badge = btn.querySelector<HTMLElement>(".sideTasksClaimBadge");
  if (count <= 0) {
    badge?.classList.add("hidden");
    badge?.setAttribute("aria-hidden", "true");
    return;
  }
  if (!badge) {
    badge = document.createElement("span");
    badge.className = "sideTasksClaimBadge";
    badge.textContent = "CLAIM";
    btn.appendChild(badge);
  }
  badge.classList.remove("hidden");
  badge.setAttribute("aria-hidden", "false");
}
